import { useDispatch, useSelector } from 'react-redux';
import { pickAFileSliceDispatch } from "../../src/Constants/ReduxConstants";
import FormEngine from "../../src/DigitalForms/FormEngine";
import { checkIfObjectIsEmpty } from "../../src/Helpers/Calculations";

export const ReduxHelpers = () => {
    const currentServiceList = useSelector(state => state.pickAFile.serviceList);
    const currentBrandList = useSelector(state => state.pickAFile.brandList);
    const currentQuestions = useSelector(state => state.pickAFile.questions);
    const currentActions = useSelector(state => state.pickAFile.actions);
    const currentRules = useSelector(state => state.pickAFile.rules);
    const currentAnswers = useSelector(state => state.pickAFile.answers);
    const currentAutoFill = useSelector(state => state.pickAFile.autoFill);
    const currentMeta = useSelector(state => state.pickAFile.meta);
    const currentDetails = useSelector(state => state.pickAFile.details);
    const currentLayout = useSelector(state => state.pickAFile.layout);
    const currentServiceQuestions = useSelector(state => state.pickAFile.serviceQuestions);
    const currentServiceBrandQuestions = useSelector(state => state.pickAFile.serviceBrandQuestions);

    const currentBaseName = useSelector(state => state.pickAFile.baseName);
    const currentAddressStart = useSelector(state => state.pickAFile.addressStart);
    const currentAddressEnd = useSelector(state => state.pickAFile.addressEnd);
    const currentFormName = useSelector(state => state.pickAFile.formName);
    const currentInterfaceId = useSelector(state => state.pickAFile.interfaceId);
    const currentAssistanceFormId = useSelector(state => state.pickAFile.assistanceFormId);
    const currentFormDescription = useSelector(state => state.pickAFile.formDescription);
    const currentFormRuleSetId = useSelector(state => state.pickAFile.formRuleSetId);

    const currentBusinessRules = useSelector(state => state.pickAFile.businessRules);

    const dispatch = useDispatch()

    const CreateContentsFromStates = (params) => {
        let serviceList = params?.serviceList ?? currentServiceList;
        let serviceQuestions = params?.serviceQuestions ?? currentServiceQuestions;
        let serviceBrandQuestions = params?.serviceBrandQuestions ?? currentServiceBrandQuestions;
        let brandList = params?.brandList ?? currentBrandList;
        let questions = params?.questions ?? currentQuestions;
        // A bug previously allowed formid to be stored as string, we have to clean these up in any files we save
        questions = questions.map(q => {
            q.formId = parseInt(q.formId);
            return q;
        });
        let layout = params?.layout ?? currentLayout;
        let rules = params?.rules ?? currentRules;
        let answers = params?.answers ?? currentAnswers;
        let actions = params?.actions ?? currentActions;
        // Ensure actionId is properly maintained as integer and auto-fix any zero values
        let addressStart = params?.addressStart ?? currentAddressStart;
        let nextActionId = parseInt(addressStart) || 1;
        
        // First pass: find the highest valid actionId to determine starting point for fixes
        const validActionIds = actions
            .map(a => parseInt(a.actionId) || 0)
            .filter(id => id > 0);
        
        if (validActionIds.length > 0) {
            nextActionId = Math.max(nextActionId, Math.max(...validActionIds) + 1);
        }
        
        let hasInvalidIds = false;
        actions = actions.map(a => {
            const currentActionId = parseInt(a.actionId) || 0;
            
            // If actionId is 0 or invalid, auto-assign a new sequential ID
            if (currentActionId === 0) {
                console.warn('Action found with invalid actionId, auto-assigning ID:', nextActionId, a);
                a.actionId = nextActionId++;
                hasInvalidIds = true;
            } else {
                a.actionId = currentActionId;
            }
            
            a.ruleId = parseInt(a.ruleId) || 0;
            return a;
        });
        
        if (hasInvalidIds) {
            console.log('Auto-fixed invalid actionIds in loaded file. Actions now have sequential IDs.');
        }
        let autofill = params?.autofill ?? currentAutoFill;
        let details = params?.details ?? currentDetails;
        let meta = params?.meta ?? currentMeta;
        addressStart = params?.addressStart ?? currentAddressStart;
        let addressEnd = params?.addressEnd ?? currentAddressEnd;

        let baseName = params?.baseName ?? currentBaseName;
        let interfaceId = params?.interfaceId ?? currentInterfaceId;
        let assistanceFormId = params?.assistanceFormId ?? currentAssistanceFormId;
        let formName = params?.formName ?? currentFormName;
        let formDescription = params?.formDescription ?? currentFormDescription;
        let formRuleSetId = params?.formRuleSetId ?? currentFormRuleSetId;

        let businessRules = params?.businessRules ?? currentBusinessRules;

        questions = sortByThenBy(questions, ["questionId"])
        serviceQuestions = sortByThenBy(serviceQuestions, ["questionId", "assistanceServiceId"])
        serviceBrandQuestions = sortByThenBy(serviceBrandQuestions, ["questionId", "assistanceServiceId", "brandId"])
        brandList = sortByThenBy(brandList, ["brandId"])
        serviceList = sortByThenBy(serviceList, ["serviceId"])

        let dataFile = {
            questions, layout, rules, actions, autofill, details, meta,
            serviceQuestions, serviceBrandQuestions, brandList, serviceList, addressStart,
            addressEnd, baseName, interfaceId, assistanceFormId, formName, formRuleSetId, formDescription,
            businessRules
        }

        let dataFileText = JSON.stringify(dataFile, null, "\t");

        const ctx = new FormEngine(
            questions,
            layout,
            rules,
            actions,
            autofill,
            checkIfObjectIsEmpty(answers) ? autofill : [],
            meta,
            {},
            {},
            details,
            {}
        );

        dispatch({ type: pickAFileSliceDispatch.setFormContext, payload: ctx })
        dispatch({ type: pickAFileSliceDispatch.setFileContents, payload: dataFileText })
    }

    const filterQuestions = (brandValue, serviceValue, questionsOverride) => {
        let allValidQuestions = []
        let questionsToUse = !currentQuestions ? currentQuestions.length > 0 ?
            currentQuestions : questionsOverride : questionsOverride;

        if ((!brandValue || brandValue?.length == 0) && serviceValue?.length > 0) {

            //Filter only by service if it's all we have

            let nonServiceQuestions = questionsToUse?.filter(q =>
                !(currentServiceQuestions?.some(sqb => sqb.questionId === q.questionId)))

            let serviceQuestionsFiltered = currentServiceQuestions.filter(sq => serviceValue.includes(sq.assistanceServiceId))

            let serviceFiltered = questionsToUse?.filter(q => serviceQuestionsFiltered?.some(b => b.questionId === q.questionId))

            questionsToUse = allValidQuestions?.concat(serviceFiltered, nonServiceQuestions)
        }

        if (brandValue && serviceValue?.length == 0) {

            let nonServiceQuestions = questionsToUse?.filter(q =>
                !(currentServiceBrandQuestions?.some(sqb => sqb.questionId === q.questionId)))

            //Filter only by drug brand if only thing selected
            let filteredBrandQuestions = currentServiceBrandQuestions?.filter(brand =>
                brandValue == brand.brandId);

            let brandFiltered = questionsToUse?.filter(q => filteredBrandQuestions?.some(b => b.questionId === q.questionId))

            questionsToUse = allValidQuestions?.concat(brandFiltered, nonServiceQuestions)
        }

        if (brandValue && serviceValue?.length > 0) {
            //Show a question if it is not contained in either list of serviceQuestions or serviceQuestionsBrand
            let nonServiceOrBrandQuestions = questionsToUse?.filter(q =>
                !(currentServiceQuestions?.some(sq => sq.questionId === q.questionId) ||
                    currentServiceBrandQuestions?.some(sqb => sqb.questionId === q.questionId))
            )
            let filteredBrandQuestions = []
            let filteredServiceQuestions = []

            if (brandValue && serviceValue.length > 0) {
                filteredBrandQuestions = currentServiceBrandQuestions?.filter(brand =>
                    brandValue == brand.brandId &&
                    serviceValue?.some(s => parseInt(s) == brand.assistanceServiceId));
            }

            if (serviceValue.length > 0) {
                filteredServiceQuestions = currentServiceQuestions?.filter(
                    service => serviceValue?.some(s => parseInt(s) == service.assistanceServiceId));
            }

            let brandFiltered = questionsToUse?.filter(q =>
                filteredBrandQuestions?.some(b => b.questionId === q.questionId))

            let serviceFiltered = questionsToUse?.filter(q =>
                filteredServiceQuestions?.some(b => b.questionId === q.questionId))

            questionsToUse = nonServiceOrBrandQuestions?.concat(brandFiltered, serviceFiltered)
        }

        //Look for empty sections in the layout
        let questionsDataNames = questionsToUse.map(q => q.dataKey)

        let filteredLayout = JSON.parse(JSON.stringify(currentLayout))
        filteredLayout = filteredLayout.filter(cl => cl.fields.some(f => questionsDataNames.includes(f)))

        filteredLayout.forEach(fl => {
            let filteredFields = fl.fields.filter(l => questionsToUse.some(q => q.dataKey == l))
            fl.fields = filteredFields
        }
        )

        return { filteredLayout, questionsToUse };
    }

    function sortByThenBy(array, keys) {
        return array?.sort(function (a, b) {
            var r = 0;
            keys.some(function (k) {
                return r = a[k] - b[k];
            });
            return r;
        });
    }

    return { CreateContentsFromStates, filterQuestions }
}

export default ReduxHelpers;